import { Routes, Route, NavLink, BrowserRouter } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { api, deviceMode } from './utils/api'
import { initI18n, t, type Lang } from './i18n/index'
import { toggleTheme, getVars, type ThemeName } from './i18n/theme'
import type { ExportTask } from './types'

import ImportPage from './pages/ImportPage'
import TimelinePage from './pages/TimelinePage'
import EditPage from './pages/EditPage'
import ExportPage from './pages/ExportPage'

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [device, setDevice] = useState<'car' | 'web'>('web')
  const [theme] = useState<ThemeName>(() => (localStorage.getItem('teslacam_theme') === 'dark' ? 'dark' : 'light'))
  const [lang, setLangState] = useState<Lang>(() => ((localStorage.getItem('teslacam_lang') as Lang) || 'zh-CN'))
  const [taskCount, setTaskCount] = useState(0)

  useEffect(() => {
    initI18n()
    const dm = deviceMode()
    setDevice(dm)
    if (dm === 'car') {
      const poll = async () => {
        try {
          const res = await api.listTasks() as { tasks: ExportTask[] }
          setTaskCount((res.tasks || []).filter((t: any) => t.status === 'processing' || t.status === 'pending').length)
        } catch {}
      }
      poll()
      const timer = setInterval(poll, 5000)
      return () => clearInterval(timer)
    }
  }, [])

  const vars = getVars(theme)
  const navItems = device === 'car'
    ? [{ path: '/timeline', label: t('nav.timeline') }, { path: '/edit', label: t('nav.edit') }, { path: '/export', label: t('nav.export') }]
    : [{ path: '/', label: t('nav.import'), exact: true }, { path: '/timeline', label: t('nav.timeline') }, { path: '/edit', label: t('nav.edit') }, { path: '/export', label: t('nav.export') }]
  const navIcons: Record<string, string> = { '/': '◇', '/timeline': '◈', '/edit': '◉', '/export': '◆' }

  return (
    <BrowserRouter>
      <div className="flex h-screen overflow-hidden relative" style={{ background: vars.bg, color: vars.text }}>
        {/* Sidebar */}
        <aside className={`flex flex-col transition-all duration-200 z-10 ${sidebarOpen ? 'w-52' : 'w-14'}`} style={{ background: vars.sidebarBg, borderRight: `1px solid ${vars.border}` }}>
          <div className="p-4 border-b" style={{ borderColor: vars.border }}>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 relative flex-shrink-0">
                <div className="absolute inset-0" style={{ background: vars.text, clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }} />
                <div className="absolute inset-1.5" style={{ background: vars.primary, clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }} />
              </div>
              {sidebarOpen && (
                <div>
                  <div className="font-mono text-sm font-black tracking-[0.2em]" style={{ color: vars.text }}>TESLACAM</div>
                  <div className="font-mono text-[9px]" style={{ color: vars.textMuted }}>v0.3.0 · {device === 'car' ? 'CAR' : 'WEB'}</div>
                </div>
              )}
            </div>
          </div>
          <nav className="flex-1 py-3 px-2">
            {navItems.map((item) => (
              <NavLink key={item.path} to={item.path} end={item.exact}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 mb-0.5 rounded-sm transition-all duration-100 border-l-2 ${isActive ? 'border-cyber-primary bg-cyber-primary/10' : 'border-transparent'} ${!sidebarOpen ? 'justify-center px-0' : ''}`}
                style={{ color: vars.textSecondary }}
              >
                <span className="text-base w-5 text-center flex-shrink-0">{navIcons[item.path]}</span>
                {sidebarOpen && <span className="font-mono text-xs font-bold uppercase tracking-widest" >{item.label}</span>}
                {item.path === '/export' && taskCount > 0 && (
                  <span className="ml-auto font-mono text-[10px] font-bold px-1.5 py-0.5 rounded-sm" style={{ background: vars.text, color: vars.primary }}>{taskCount}</span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t" style={{ borderColor: vars.border }}>
            {sidebarOpen ? (
              <div className="flex items-center gap-2 px-2 py-2 rounded-sm" style={{ background: vars.inputBg }}>
                <div className="w-1.5 h-1.5 rounded-sm animate-pulse flex-shrink-0" style={{ background: vars.primary }} />
                <span className="font-mono text-[10px] flex-1 uppercase tracking-wider" style={{ color: vars.textMuted }}>{device === 'car' ? 'CAR_MODE' : 'WEB_MODE'}</span>
              </div>
            ) : (
              <div className="flex justify-center"><div className="w-1.5 h-1.5 rounded-sm animate-pulse" style={{ background: vars.primary }} /></div>
            )}
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 flex flex-col overflow-hidden relative z-0">
          <header className="h-12 border-b flex items-center justify-between px-6 flex-shrink-0" style={{ background: vars.topbarBg, borderBottomColor: vars.border, backdropFilter: 'blur(12px)' }}>
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-1.5 rounded-sm transition-colors hover:opacity-80" style={{ color: vars.textSecondary }}>
                {sidebarOpen ? '◀' : '▶'}
              </button>
              <div>
                <div className="font-mono text-xs font-black uppercase tracking-[0.2em]" style={{ color: vars.text }}>
                  {device === 'car' ? 'EDIT // TESLACAM' : 'TESLACAM // VIDEO MANAGEMENT'}
                </div>
                <div className="font-mono text-[10px]" style={{ color: vars.textMuted }}>
                  {new Date().toISOString().slice(0, 10)} // CAR_API v0.3.0
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Language */}
              <div className="flex items-center rounded-sm overflow-hidden border" style={{ borderColor: vars.border }}>
                {(['zh-CN', 'en', 'zh-TW'] as const).map((l) => (
                  <button key={l} onClick={() => setLangState(l)}
                    className="px-2 py-1.5 font-mono text-[10px] font-bold uppercase transition-colors"
                    style={{ background: lang === l ? vars.primary : 'transparent', color: lang === l ? '#0A0A0A' : vars.textMuted }}>
                    {l === 'zh-CN' ? '简' : l === 'en' ? 'EN' : '繁'}
                  </button>
                ))}
              </div>
              {/* Theme */}
              <button onClick={toggleTheme}
                className="px-2 py-1.5 rounded-sm font-mono text-[10px] font-bold uppercase border transition-colors hover:opacity-80"
                style={{ borderColor: vars.border, color: vars.textMuted, background: 'transparent' }}>
                {theme === 'dark' ? '◐ DARK' : '◑ LIGHT'}
              </button>
              {/* Status */}
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-sm" style={{ background: vars.primaryDim, border: `1px solid ${vars.primary}30` }}>
                <div className="w-1.5 h-1.5 rounded-sm animate-pulse" style={{ background: vars.primary }} />
                <span className="font-mono text-[10px] font-bold uppercase tracking-wider" style={{ color: '#00897B' }}>ONLINE</span>
              </div>
            </div>
          </header>
          <main className="flex-1 overflow-auto p-6" style={{ background: vars.bg }}>
            <Routes>
              <Route path="/" element={<ImportPage />} />
              <Route path="/timeline" element={<TimelinePage />} />
              <Route path="/edit" element={<EditPage />} />
              <Route path="/export" element={<ExportPage />} />
            </Routes>
          </main>
        </div>
      </div>
    </BrowserRouter>
  )
}

export default App
